import { FormInstance, Spin, message } from "antd";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import {
  APIError,
  FormFieldConfig,
  AssetAllocation,
  Product,
  SelectOptionsInterface,
} from "../helpers/shared-interfaces";
import Card from "../components/Card";
import { AxiosResponse } from "axios";
import {
  UPDATE_ASSET_ALLOCATION,
  GET_ASSET_ALLOCATION,
  GET_ASSET_ALLOCATION_ITEMS,
  GET_ASSET_ALLOCATION_RATES,
} from "../gql/economicIndicators";
import * as Sentry from "@sentry/browser";
import BaseForm from "../components/Form/BaseForm";
import debounce from "lodash.debounce";
import { GET_PRODUCTS } from "../gql/products";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface RouteParams {
  id: string;
}

interface State {
  id?: number;
  name: string;
  isLoading: boolean;
  page: number;
  q: string;
  products: Product[];
  assetAllocationItems: SelectOptionsInterface[];
  assetAllocationRates: SelectOptionsInterface[];
  quarterId: string | null;
  assetAllocationItemId?: number;
  assetAllocationRateId?: number;
  enable?: boolean;
  productId?: string;
  assetAllocation?: AssetAllocation;
}

export default class EditAssetAllocation extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: RouteComponentProps<RouteParams>) {
    super(props);
    this.state = {
      name: "",
      isLoading: true,
      page: 1,
      q: "",
      products: [],
      assetAllocationItems: [],
      assetAllocationRates: [],
      quarterId: null,
    };
    this.reference = React.createRef<FormInstance>();
    this.updateAssetAllocation = this.updateAssetAllocation.bind(this);
    this.getAssetAllocationItems = this.getAssetAllocationItems.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: parseInt(this.props.match.params.id) }, () => {
      this.getAssetAllocationItems();
      this.getAssetAllocationRates();
      this.getProducts();
      this.getAssetAllocation();
    });
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  getAssetAllocation = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_ASSET_ALLOCATION,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const assetAllocation = data.data?.assetAllocation;
            this.setState({
              assetAllocation,
              quarterId: assetAllocation?.quarter?.id,
              enable: assetAllocation?.enable,
            });
          } else {
            message.error("Failed to get asset allocation data");
          }
        });
      },
    );
  };

  getAssetAllocationItems = async (): Promise<void> => {
    this.setState({ isLoading: true });
    const { page } = this.state;

    await request({
      query: GET_ASSET_ALLOCATION_ITEMS,
      variables: { page },
    })
      .then((response: any): void => {
        const { data } = response;
        this.setState({
          assetAllocationItems: data.data?.assetAllocationItems,
          isLoading: false,
        });
      })
      .catch((error) => {
        message.error("Failed To Load Asset Allocation Items");
        Sentry.captureException(error);
      });

    this.setState({ isLoading: false });
  };

  getAssetAllocationRates = async (): Promise<void> => {
    this.setState({ isLoading: true });
    const { page } = this.state;

    await request({
      query: GET_ASSET_ALLOCATION_RATES,
      variables: { page },
    })
      .then((response: any): void => {
        const { data } = response;
        this.setState({
          assetAllocationRates: data.data?.assetAllocationRates,
          isLoading: false,
        });
      })
      .catch((error) => {
        message.error("Failed To Load Asset Allocation Items");
        Sentry.captureException(error);
      });

    this.setState({ isLoading: false });
  };

  getProducts = (): void => {
    const { q } = this.state;
    interface Response {
      data: {
        products: {
          results: Product[];
        };
      };
    }

    request({
      query: GET_PRODUCTS,
      variables: {
        page: 1,
        q,
      },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({ products: data.data?.products?.results });
    });
  };

  redirectToViewQuarterPage(): void {
    this.props.history.push(`/quarters/${this.state.quarterId}`);
  }

  redirectToEditQuarterPage(): void {
    this.props.history.push(`/quarters/${this.state.quarterId}/edit`);
  }

  async updateAssetAllocation(): Promise<void> {
    const { id, quarterId, enable } = this.state;
    interface Response {
      assetAllocation: AssetAllocation;
      errors: APIError[];
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields([
        "productId",
        "assetAllocationItemId",
        "assetAllocationRateId",
        "enable",
        "order",
        "rate",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_ASSET_ALLOCATION,
        variables: {
          id,
          quarterId,
          ...data,
          enable,
        },
      });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "updateAssetAllocation",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.setState({ id: mutation?.assetAllocation?.id }, () => {
          message.success("Asset Allocation updated sucessfully");
          this.redirectToEditQuarterPage();
        });
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to update asset allocation - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  handleAssetAllocationItemSelect = async (assetAllocationItemId: string) => {
    const selectedAsset = this.state.assetAllocationItems.find(
      (asset) => asset.id.toString() === assetAllocationItemId,
    );

    if (selectedAsset) {
      const parsedId = parseInt(selectedAsset.id);
      if (!isNaN(parsedId)) {
        this.setState({ assetAllocationItemId: parsedId });
      } else {
        console.error("Failed to parse asset id as integer.");
      }
    } else {
      console.error("Selected asset not found.");
    }
  };

  handleAssetAllocationRateSelect = async (assetAllocationRateId: string) => {
    const selectedAsset = this.state.assetAllocationItems.find(
      (asset) => asset.id.toString() === assetAllocationRateId,
    );

    if (selectedAsset) {
      const parsedId = parseInt(selectedAsset.id);
      if (!isNaN(parsedId)) {
        this.setState({ assetAllocationItemId: parsedId });
      } else {
        console.error("Failed to parse asset id as integer.");
      }
    } else {
      console.error("Selected asset not found.");
    }
  };

  handleSearchInput = async (q: string): Promise<void> => {
    this.setState({ q });
    await this.triggerDelayedSearch();
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    await this.getProducts();
  }, DEBOUNCE_TIME);

  changeEnable = async (checkedValue: boolean) => {
    this.setState({
      enable: checkedValue,
    });
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      products,
      assetAllocationItems,
      assetAllocationRates,
      assetAllocation,
      enable,
    } = this.state;

    const orders: SelectOptionsInterface[] = [
      {
        id: "n/a",
        name: "n/a",
      },
      {
        id: "1st",
        name: "1st",
      },
      {
        id: "2nd",
        name: "2nd",
      },
      {
        id: "3rd",
        name: "3rd",
      },
      {
        id: "4th",
        name: "4th",
      },
      {
        id: "5th",
        name: "5th",
      },
      {
        id: "6th",
        name: "6th",
      },
      {
        id: "7th",
        name: "7th",
      },
      {
        id: "8th",
        name: "8th",
      },
      {
        id: "9th",
        name: "9th",
      },
      {
        id: "10th",
        name: "10th",
      },
    ];

    const fields: FormFieldConfig[] = [
      {
        name: "assetAllocationItemId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Asset",
        placeholder: "Asset",
        initialValue: assetAllocation?.assetAllocationItem?.id,
        changeHandler: this.handleAssetAllocationItemSelect,
        selectHandler: this.handleAssetAllocationItemSelect,
        selectOptions: assetAllocationItems,
        colConfig: {
          span: 7,
        },
      },
      {
        name: "assetAllocationRateId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Rate Type",
        placeholder: "Rate Type",
        initialValue: assetAllocation?.assetAllocationRate?.id,
        changeHandler: this.handleAssetAllocationRateSelect,
        selectHandler: this.handleAssetAllocationRateSelect,
        selectOptions: assetAllocationRates,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "rate",
        name: "rate",
        rule: "number",
        inputType: "number",
        required: true,
        label: "Rate %",
        initialValue: assetAllocation?.rate,
        placeholder: "Rate %",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        name: "productId",
        rule: "string",
        inputType: "search",
        required: true,
        label: "Scheme",
        placeholder: "Scheme",
        initialValue: assetAllocation?.product?.id,
        changeHandler: this.handleSearchInput,
        selectOptions: products,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "order",
        name: "order",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Order",
        placeholder: "Order",
        initialValue: assetAllocation?.order,
        selectOptions: orders,
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "enable",
        name: "enable",
        rule: "boolean",
        inputType: "checkbox",
        required: false,
        label: "Enable",
        checkboxCheckedValue: enable,
        checkboxChangeHandler: (e) => this.changeEnable(e.target.checked),
        colConfig: {
          span: 7,
          offset: 1,
        },
      },
      {
        id: "updateAssetAllocation",
        name: "updateAssetAllocationButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.updateAssetAllocation,
      },
    ];

    return (
      <Card header="EDIT ASSET ALLOCATION">
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              reference={this.reference}
              fieldsToHide={[]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
